import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

import { getMediaUrl, getDisplayName, formatMessageTime, isCustomCharacter } from '../lib/utils';
import { Skeleton } from './ui/skeleton';

import { Button } from './ui/button';
import { useUserInfo } from '@/hooks/useUserInfo';

export interface ChatListItem {
  id: string;
  created_at: string;
  character_id: string | null;
  character: {
    url_slug?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    avatar_fpath?: string | null;
    profile_media_fpaths?: string[] | null;
  };
  chat_metadata?: {
    pending_video_at?: string | null;
    pending_video_origin_msg_id?: string | null;
    pending_video_mr_id?: string | null;
  } | null;
  lastMessage?: {
    msg_text?: string | null;
    msg_type?: string | null;
    created_at?: string;
  };
}

interface ChatListViewProps {
  chats: ChatListItem[];
  loading?: boolean;
  error?: string | null;
  activeChatId?: string | null;
  onSelect: (chatIdOrCharSlug: string) => void;
  onDelete: (chatId: string) => void;
  onClose?: () => void; // for mobile/tablet overlay
}

export const ChatListView: React.FC<ChatListViewProps> = ({
  chats,
  loading = false,
  error,
  activeChatId,
  onSelect,
  onDelete,
  onClose,
}) => {
  const { characters } = useUserInfo();

  const lastMessageSummary = (chat: ChatListItem) => {
    const lastMessage = chat.lastMessage;
    const empty = <span className="italic text-zinc-600">No messages yet</span>;
    if (!lastMessage) return empty;
    if (lastMessage.msg_type === 'call_summary') return 'Call';
    if (lastMessage.msg_text) return lastMessage.msg_text;
    if (lastMessage.msg_type === 'image') return 'Image';
    if (lastMessage.msg_type !== 'text') return 'Media';
    return empty;
  };

  const displayedCharacters = characters.filter(c => {
      const isCustomCharWithImg = isCustomCharacter(c) && c.profile_media_fpaths?.length;
      const hasNoChats = !chats.some(chat => chat.character_id === c.id);

      return isCustomCharWithImg && hasNoChats;
    })
    .map((character) => {
      const avatar = character.avatar_fpath
        ? getMediaUrl(character.avatar_fpath)
        : character.profile_media_fpaths?.[0]
        ? getMediaUrl(character.profile_media_fpaths[0])
        : undefined;
      return {
        ...character,
        avatar,
      };
    });

  const renderNavigationCTAs = () => {
    return (
      <>
        <Button
          className="text-base bg-amber-600 hover:bg-amber-500 rounded-lg text-white font-bold p-3 flex items-center justify-center"
          asChild
        >
          <Link to="/">
            Explore Characters
          </Link>
        </Button>
        <Button
          className="text-base bg-pink-700 hover:bg-pink-600 rounded-lg text-white font-bold p-3 flex items-center justify-center"
          asChild
        >
          <Link to="/create-character">
            <Sparkles className="size-4 sm:size-5" />
            <span>Create Your AI</span>
          </Link>
        </Button>
      </>
    )
  }

  return (
    <div className="flex flex-col h-full w-full bg-zinc-900 text-white">
      {/* Close button for mobile/tablet */}
      {onClose && (
        <div className="relative">
          <button
            className="absolute top-1 right-6 z-10 bg-zinc-700 rounded-full py-2 px-4 hover:bg-zinc-600 opacity-70"
            onClick={onClose}
            aria-label="Close chat list"
          >
            <span className="text-2xl">×</span>
          </button>
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-2 max-sm:pt-8">
        {loading ? (
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900">
                <Skeleton className="rounded-full size-14 bg-zinc-700" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-32 bg-zinc-700 mb-2" />
                  <Skeleton className="h-3 w-40 bg-zinc-800" />
                </div>
                <Skeleton className="size-8 rounded-full bg-zinc-700" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-400">{error}</div>
        ) : chats.length === 0 ? (
          <div className="p-4 text-center text-zinc-400 flex flex-col items-center gap-4">
            <div>No conversations yet.</div>
            <div className="flex flex-col gap-4">
              {renderNavigationCTAs()}
            </div>
          </div>
        ) : (
          <>
          <ul className="space-y-2">
            {chats.map((chat) => {
              const avatar = chat.character.avatar_fpath
                ? getMediaUrl(chat.character.avatar_fpath)
                : chat.character.profile_media_fpaths?.[0]
                ? getMediaUrl(chat.character.profile_media_fpaths[0])
                : undefined;
              return (
                <li
                  key={chat.id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    chat.id === activeChatId ? 'bg-zinc-800' : 'hover:bg-zinc-900'
                  }`}
                  onClick={() => onSelect(chat.id)}
                  tabIndex={0}
                  aria-label={`Open chat with ${getDisplayName(chat.character)}`}
                >
                  <img
                    src={avatar}
                    alt={getDisplayName(chat.character) || 'Avatar'}
                    className="rounded-full size-14 object-cover bg-zinc-700"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{getDisplayName(chat.character)}</div>
                    <div className="text-zinc-400 text-sm truncate">
                      {lastMessageSummary(chat)}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500">
                        {formatMessageTime(chat.lastMessage?.created_at)}
                      </span>
                      <button
                        className="ml-auto text-zinc-400/40 hover:text-pink-500 p-1 rounded-full size-6 flex items-center justify-center"
                        onClick={e => {
                          e.stopPropagation();
                          onDelete(chat.id);
                        }}
                        aria-label="Delete chat"
                        tabIndex={0}
                      >
                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                          <path 
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 6h18M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          {chats.length < 3 && (
            <div className="flex flex-col gap-4 p-4">
              {renderNavigationCTAs()}
            </div>
          )}
          </>
        )}
        {displayedCharacters && displayedCharacters.length > 0 && (
          <div className="flex flex-col gap-2 py-2">
            <div className="px-2 mt-4 text-zinc-400 text-sm uppercase">My Characters</div>
            <ul className="space-y-2">
              {displayedCharacters.map((character) => {
                return (
                  <li
                    key={character.id}
                    tabIndex={0}
                    aria-label={`Open chat with ${getDisplayName(character)}`}
                    onClick={() => onSelect(character.url_slug)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors hover:bg-zinc-900"
                  >
                    <img src={character.avatar} alt={getDisplayName(character)} className="rounded-full size-14 object-cover bg-zinc-700" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{getDisplayName(character)}</div>
                      <div className="text-zinc-400 text-sm truncate">
                        {/* TODO: pick according to settings */}
                        {character.chat_starter_im || character.chat_starter_rp}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatListView; 